import React, { useEffect, useMemo, useState } from "react";
import {
    CapabilityDetail,
    CapabilityList,
    CapabilityName,
    CapabilityRow,
    CapabilityStatus,
    CapabilityText,
    DeviceSelect,
    FieldGroup,
    SettingsPanel,
    SettingsTitle
} from "src/scenes/Chat/components/ChatCallPanel.styled";
import { deviceLabel, getBrowserCallCapabilities } from "./ChatCallPanel.constants";

const LiveSettingsPanel = ({
    cameraDeviceId,
    microphoneDeviceId,
    onSetCameraDevice,
    onSetMicrophoneDevice,
    onSetSpeakerDevice,
    speakerDeviceId
}) => {
    const [devices, setDevices] = useState([]);
    const [isChanging, setIsChanging] = useState(false);
    const canSelectSpeaker = typeof HTMLMediaElement !== "undefined" &&
        Boolean(HTMLMediaElement.prototype.setSinkId);

    useEffect(() => {
        let cancelled = false;

        const loadDevices = async () => {
            try {
                const nextDevices = await navigator.mediaDevices?.enumerateDevices?.();
                if (!cancelled) {
                    setDevices(nextDevices || []);
                }
            } catch (error) {
                console.warn("Unable to enumerate live call devices.", error);
            }
        };

        loadDevices();
        navigator.mediaDevices?.addEventListener?.("devicechange", loadDevices);
        return () => {
            cancelled = true;
            navigator.mediaDevices?.removeEventListener?.("devicechange", loadDevices);
        };
    }, []);

    const deviceGroups = useMemo(() => ({
        microphones: devices.filter(device => device.kind === "audioinput"),
        speakers: devices.filter(device => device.kind === "audiooutput"),
        cameras: devices.filter(device => device.kind === "videoinput")
    }), [devices]);
    const browserCapabilities = useMemo(() => getBrowserCallCapabilities(), []);
    const unsupportedCapabilities = useMemo(
        () => browserCapabilities.filter(capability => !capability.supported),
        [browserCapabilities]
    );

    const changeDevice = async (handler, value) => {
        setIsChanging(true);
        try {
            await handler?.(value);
        } finally {
            setIsChanging(false);
        }
    };

    return (
        <SettingsPanel>
            <SettingsTitle>Settings</SettingsTitle>
            <FieldGroup>
                Microphone
                <DeviceSelect
                    disabled={isChanging}
                    value={microphoneDeviceId}
                    onChange={event => changeDevice(onSetMicrophoneDevice, event.target.value)}
                >
                    <option value="">Default microphone</option>
                    {deviceGroups.microphones.map((device, index) => (
                        <option key={device.deviceId} value={device.deviceId}>
                            {deviceLabel(device, `Microphone ${index + 1}`)}
                        </option>
                    ))}
                </DeviceSelect>
            </FieldGroup>
            {canSelectSpeaker &&
                <FieldGroup>
                    Speaker
                    <DeviceSelect
                        disabled={isChanging}
                        value={speakerDeviceId}
                        onChange={event => changeDevice(onSetSpeakerDevice, event.target.value)}
                    >
                        <option value="">Default speaker</option>
                        {deviceGroups.speakers.map((device, index) => (
                            <option key={device.deviceId} value={device.deviceId}>
                                {deviceLabel(device, `Speaker ${index + 1}`)}
                            </option>
                        ))}
                    </DeviceSelect>
                </FieldGroup>
            }
            <FieldGroup>
                Camera
                <DeviceSelect
                    disabled={isChanging}
                    value={cameraDeviceId}
                    onChange={event => changeDevice(onSetCameraDevice, event.target.value)}
                >
                    <option value="">Default camera</option>
                    {deviceGroups.cameras.map((device, index) => (
                        <option key={device.deviceId} value={device.deviceId}>
                            {deviceLabel(device, `Camera ${index + 1}`)}
                        </option>
                    ))}
                </DeviceSelect>
            </FieldGroup>
            {unsupportedCapabilities.length > 0 && (
                <>
                    <SettingsTitle>Browser Limitations</SettingsTitle>
                    <CapabilityList>
                        {unsupportedCapabilities.map(capability => (
                            <CapabilityRow key={capability.label}>
                                <CapabilityText>
                                    <CapabilityName>{capability.label}</CapabilityName>
                                    <CapabilityDetail>{capability.detail}</CapabilityDetail>
                                </CapabilityText>
                                <CapabilityStatus $supported={false}>Limited</CapabilityStatus>
                            </CapabilityRow>
                        ))}
                    </CapabilityList>
                </>
            )}
        </SettingsPanel>
    );
};

export default LiveSettingsPanel;
